// ตารางสอนครูรายคน (ภาคศาสนา) — เลือกครูแล้วดูตารางของคนนั้นทุกห้องในปีการศึกษา
//
// คู่ขนานกับ teaching/TeacherTimetable.js ของภาคสามัญ ต่างกันที่ไม่มีเทอม (is_subject
// ไม่มีคอลัมน์ภาคเรียน) และช่องหนึ่งมีได้หลายห้อง เพราะหน้านี้มีไว้จับคาบที่จัดซ้อนกัน
import React, { useEffect, useMemo, useState } from "react";
import { Card, Button, Form, Badge } from "react-bootstrap";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import {
  teacherLabel,
  teacherOptionLabel,
  buildTeacherGrid,
  shortTime,
  exportTimetablePdf,
  printTimetableDoc,
} from "../utils/islamicTimetable";
import { TeacherTimetableGrid, teacherPrintDoc } from "./teacherTimetableView";

const API = process.env.REACT_APP_API;

export default function IslamicTeacherTimetable() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === "th" ? th : jawi;
  const tt = t.teacherTimetable;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [teacherId, setTeacherId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then((res) => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axiosInstance.get(`${API}/getTeacherList?side=islamic`)
      .then((res) => setTeacherList(res.data || []))
      .catch(() => setTeacherList([]));
  }, []);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`)
      .then((res) => setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: shortTime(p.period_begin),
        period_end: shortTime(p.period_end),
      }))))
      .catch(() => setPeriods([]));
  }, [yearId]);

  useEffect(() => {
    if (!yearId || !teacherId) { setRows([]); return; }
    setLoading(true);
    axiosInstance.get(`${API}/islamic-teaching/teacherTimetable/${teacherId}/${yearId}`)
      .then((res) => setRows(res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [yearId, teacherId, setLoading]);

  const grid = useMemo(() => buildTeacherGrid(rows), [rows]);

  // เรียงตามชื่อที่แสดงจริง — เมนูไทยเรียงตามอักษรไทย เมนูยาวีเรียงตามอักษรอาหรับ
  // ชื่อในฐานข้อมูลมีช่องว่างนำหน้า/ต่อท้ายปนอยู่ ต้อง trim ก่อนเทียบ ไม่งั้นแถวพวกนั้น
  // จะถูกดันขึ้นไปอยู่บนสุดก่อนตัวอักษรทั้งหมด (เหมือน teaching/TeacherTimetable.js)
  const teacherOptions = useMemo(() => {
    const locale = lang === "th" ? "th" : "ar";
    return teacherList
      .map((tc) => ({
        t_id: tc.t_id,
        sortKey: teacherLabel(lang, tc).name.trim(),
        label: teacherOptionLabel(lang, tc),
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey, locale));
  }, [teacherList, lang]);

  const selected = teacherList.find((tc) => String(tc.t_id) === String(teacherId)) || null;
  const teacherDisplay = teacherLabel(lang, selected);

  const slotCount = rows.length;
  const hasConflict = rows.some((r) => r.has_teacher_conflict);

  const yearLabel = yearList.find((y) => String(y.year_id) === String(yearId))?.year_year || "";

  // ─── เอกสารที่สั่งพิมพ์ (ไทยเสมอ ดู ./teacherTimetableView) ────────────────
  const printDoc = () => teacherPrintDoc({ periods, grid, teacher: selected, yearLabel, lang, t });

  const hasData = rows.length > 0;

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">{tt.pageTitle}</h4>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={(e) => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map((y) => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      <Card className="card-one">
        <Card.Header className="d-flex align-items-center flex-wrap gap-2">
          <Form.Select
            value={teacherId}
            onChange={(e) => setTeacherId(e.target.value)}
            size="sm"
            style={{ minWidth: 260, maxWidth: 320 }}
            className={lang === "th" ? "" : "arabic-text"}
          >
            <option value="">{t.manageTeaching.teacherPlaceholder}</option>
            {teacherOptions.map((tc) => (
              <option key={tc.t_id} value={tc.t_id}>{tc.label}</option>
            ))}
          </Form.Select>
          {teacherId && hasData && (
            <>
              <Badge bg="light" text="dark">{tt.slotSummary(slotCount)}</Badge>
              {hasConflict && <Badge bg="danger">{tt.conflictBadge}</Badge>}
            </>
          )}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="outline-danger"
              size="sm"
              disabled={!hasData || periods.length === 0}
              onClick={() => exportTimetablePdf(printDoc())}
            >
              <i className="ri-download-2-line me-1"></i>{tt.exportPdf}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!hasData || periods.length === 0}
              onClick={() => printTimetableDoc(printDoc())}
            >
              <i className="ri-printer-line me-1"></i>{tt.print}
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-2" style={{ overflowX: "auto" }}>
          {!teacherId ? (
            <div className="text-center text-muted py-5">
              <i className="ri-user-search-line fs-2 d-block mb-2"></i>
              {tt.pickTeacher}
            </div>
          ) : periods.length === 0 ? (
            <div className="text-center text-muted py-5">{tt.noPeriods}</div>
          ) : !hasData ? (
            <div className="text-center text-muted py-5">
              <i className="ri-calendar-close-line fs-2 d-block mb-2"></i>
              {tt.noTimetable(teacherDisplay.name)}
            </div>
          ) : (
            <>
              <TeacherTimetableGrid periods={periods} grid={grid} dayNames={t.days} labels={tt} />
              {hasConflict && (
                <div className="text-danger mt-2" style={{ fontSize: "0.78rem" }}>
                  <i className="ri-alert-line me-1"></i>{tt.conflictHint}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </React.Fragment>
  );
}
